// What a custom folder points at when the project loader does not know it. The project data only holds shots
// that sit in a scene, so a shot with no placement (made on its own, or dropped into a folder before it was
// placed) used to read "No longer available" in a folder. The folder endpoint resolves every reference the
// server can still see, so the UI asks it, and falls back to the row itself for what was archived (a code and
// a last title are all an archived item keeps).
import type { Api } from "./api.ts";
import type { FolderEntry } from "./folders.ts";
import type { TakesResponse } from "./types.ts";
import type { ProjectData } from "./useProject.ts";

/** What the server said about one folder reference. */
export interface RefInfo {
  /** false when the item is archived or hidden from you */
  available: boolean;
  /** the folder endpoint's summary (code, description, size, duration for shots; name for the rest) */
  summary: Record<string, unknown> | null;
  /** the shot's takes, for its still (shots only) */
  takes: TakesResponse | null;
  /** an archived item's identity, read from its row */
  archived?: { code?: string; name?: string };
}

export const refKey = (r: { kind: string; id: string }) => `${r.kind}:${r.id}`;

/**
 * The references of a folder the project data cannot resolve (shots without a placement, archived items), and
 * every uploaded file: the project data holds no file names, the folder endpoint does (name, kind, mime).
 */
export function unresolvedRefs(d: ProjectData, items: readonly FolderEntry[]): FolderEntry[] {
  return items.filter((r) => {
    switch (r.kind) {
      case "asset":
        return true;
      case "shot":
        return !d.shotById.has(r.id);
      case "canvas":
        return !d.canvases.some((x) => x.id === r.id);
      case "element":
        return !d.elements.some((x) => x.id === r.id);
      case "episode":
        return !d.episodes.some((x) => x.id === r.id);
      case "scene":
        return ![...d.scenes.values()].some((l) => l.some((x) => x.id === r.id));
      default:
        return false;
    }
  });
}

interface ResolvedRow {
  item_type: string;
  item_id: string;
  available: boolean;
  summary: Record<string, unknown> | null;
}

const API_KIND: Record<string, string> = {
  shot: "shot",
  canvas: "canvas",
  element: "element",
  asset: "asset_version",
  scene: "scene",
  episode: "episode",
};
const ROW_PATH: Record<string, string> = { shot: "shots", canvas: "canvases", element: "elements" };

/** The identity of an archived item, from its row (archived rows stay readable). */
async function archivedIdentity(
  api: Api,
  ws: string,
  kind: string,
  id: string,
): Promise<RefInfo["archived"] | undefined> {
  const seg = ROW_PATH[kind];
  if (!seg) return undefined;
  try {
    const row = await api.request<Record<string, unknown>>("GET", `/workspaces/${ws}/${seg}/${id}`);
    const text = (v: unknown) => (typeof v === "string" && v.trim() ? v : undefined);
    return { code: text(row.code), name: text(row.description) ?? text(row.name) };
  } catch {
    return undefined;
  }
}

/** Ask the server about references; failures remain retryable, never cached as empty results. */
export async function loadRefInfo(
  api: Api,
  ws: string,
  folderId: string,
  refs: readonly FolderEntry[],
): Promise<Map<string, RefInfo>> {
  const out = new Map<string, RefInfo>();
  if (refs.length === 0) return out;
  const rows = (await api.request<{ items: ResolvedRow[] }>("GET", `/workspaces/${ws}/folders/${folderId}`))
    .items;
  const byRow = new Map(rows.map((r) => [`${r.item_type}:${r.item_id}`, r]));
  await Promise.all(
    refs.map(async (r) => {
      const row = byRow.get(`${API_KIND[r.kind] ?? r.kind}:${r.id}`);
      if (!row) return;
      const [takes, archived] = await Promise.all([
        r.kind === "shot" ? api.listShotTakes(ws, r.id) : Promise.resolve(null),
        row.available ? Promise.resolve(undefined) : archivedIdentity(api, ws, r.kind, r.id),
      ]);
      out.set(refKey(r), { available: row.available, summary: row.summary, takes, archived });
    }),
  );
  return out;
}
